import { C, H2, H3, Lead, Note, OL, P, Table, Terms, UL } from '../Doc';

export function Application() {
  return (
    <>
      <Lead>
        How to use the studio, module by module. Start with the vocabulary — the rest of this
        page and the integration guides assume it — then work through whichever module you
        need.
      </Lead>

      <H2 id="shape">How the account is shaped</H2>
      <P>
        Five levels, from the billing boundary down to a single answer. The distinction that
        matters most is the first: a tenant is an isolation boundary, a company is not.
      </P>
      <Terms
        items={[
          {
            term: 'Tenant',
            children: <>
              The account. The billing and data-isolation boundary — row-level security keys
              on the tenant alone, so two tenants can never see one another's rows.
            </>,
          },
          {
            term: 'Company',
            children: <>
              An organisational tier <em>inside</em> one tenant, for separating parts of a
              business. It partitions work; it does not separate customers. Switch with the
              control in the top bar — everything below it re-scopes.
            </>,
          },
          {
            term: 'Workspace',
            children: <>
              Lives under a company and holds forms. Every company gets a default workspace
              when it is created, so a form always has somewhere to go.
            </>,
          },
          {
            term: 'Folder',
            children: <>
              An optional grouping, shown in the left rail. A form is in exactly one folder
              or none. Folders nest; deleting one moves its forms out rather than deleting
              them.
            </>,
          },
          {
            term: 'Form, version, response',
            children: <>
              A form is edited as a draft and <strong>published</strong> to create an
              immutable version. Respondents always fill in a published version.
            </>,
          },
        ]}
      />

      <H2 id="forms">Forms list</H2>
      <P>
        Every filter is in the URL, so any view can be bookmarked or sent to a colleague.
        They combine: a folder, a type and a search term narrow together.
      </P>
      <UL>
        <li><strong>Folder</strong> — the left rail. “All forms” clears it; “Unfiled” finds what has not been filed.</li>
        <li><strong>Type</strong> — the dropdown in the header.</li>
        <li><strong>Name</strong> — the filter box.</li>
        <li><strong>Company</strong> — the switcher in the top bar, which scopes everything.</li>
      </UL>
      <P>
        Click a row to see its details on the right; double-click or press Enter to open it.
        The response column shows the total, and a seven-day sparkline when there has
        actually been activity. The panel on the right carries the share link and the common
        actions, and is where you move a form between folders.
      </P>

      <H2 id="types">Form types</H2>
      <P>
        Chosen at creation. The type shapes how the runtime addresses the respondent — the
        submit wording, the review step, and whether progress counts pages or questions.
      </P>
      <Table
        head={['Type', 'Submit button', 'Progress counted in']}
        rows={[
          ['Survey', 'Submit', 'Pages'],
          ['Quiz', 'Submit answers', 'Questions, when one per page'],
          ['Poll', 'Cast your vote', 'Pages'],
          ['Application', 'Submit application', 'Pages'],
          ['Assessment', 'Submit assessment', 'Questions, when one per page'],
          ['Digital form', 'Submit form', 'Pages'],
        ]}
      />
      <P>
        Each shows its name above the form title; blank it on one form by translating{' '}
        <C>ui.typeLabel</C> to an empty string.
      </P>

      <H2 id="builder">Builder</H2>
      <P>
        Three columns — question types, the form, and the selected question's settings — each
        scrolling independently. Add a question by clicking a type, or drag it to a precise
        position. Changes autosave; <C>⌘S</C> saves now, <C>⌘Z</C> and <C>⌘⇧Z</C> undo and
        redo.
      </P>

      <H3>Question types</H3>
      <Table
        head={['Group', 'Types']}
        rows={[
          ['Text', 'Short text, long text, email, phone, URL, number, currency, masked text'],
          ['Choice', 'Single choice, multiple choice, dropdown, searchable dropdown, image choice, yes/no, Likert'],
          ['Scale', 'Rating, NPS, slider, star, emoji scale, ranking, matrix (single and multiple)'],
          ['Date', 'Date, time, date and time'],
          ['Advanced', 'Country, file upload, signature, calculated, hidden'],
          ['Layout', 'Section header, page break, columns, repeating group'],
        ]}
      />
      <P>
        A type the runtime cannot yet draw appears greyed out rather than hidden, so the
        roadmap is visible instead of the type looking unsupported forever. Publishing a form
        that uses one is refused.
      </P>
      <P>
        Eight known types are <strong>not built</strong> and are refused at publish with an
        explanation rather than quietly rendered as a text box: points allocation
        (constant sum), best-worst (MaxDiff), matrices with typed answers, date range,
        address, map/location, and payment. Several have a workaround — two date questions
        instead of a range, separate text questions instead of an address, a choice matrix
        instead of a typed one.
      </P>

      <H3>Structure</H3>
      <Terms
        items={[
          { term: 'Pages', children: <>What the respondent moves through. Add as many as you need; logic can skip them.</> },
          { term: 'Columns', children: <>A layout group placing questions side by side. A columns block cannot nest inside another.</> },
          {
            term: 'Repeating groups',
            children: <>
              “Add another dependant”. A group the respondent repeats, with a minimum and a
              required maximum — the maximum is what bounds the width of a CSV export, which
              is why it is not optional.
            </>,
          },
        ]}
      />

      <H3>Question settings</H3>
      <UL>
        <li><strong>Required</strong>, help text, placeholder and description.</li>
        <li><strong>Validation</strong> — length, minimum and maximum, selection counts, or a regular expression with your own message.</li>
        <li><strong>Choices</strong> — including “other, please specify”, an exclusive option that clears the rest, and a display order of as-is, random or sorted.</li>
        <li><strong>Prefillable</strong> — opt in per question before an embed can supply a value.</li>
        <li><strong>PII category</strong> — name, email, phone, address, national id, health, financial or biometric. Marking it is what lets the platform treat it accordingly.</li>
      </UL>

      <H2 id="logic">Logic</H2>
      <P>
        Rules are <strong>data, not code</strong>: a trigger, an optional condition, and one
        or more actions. The same rules are evaluated by the runtime as the respondent moves,
        and again by the server at submit — so a hidden question cannot be answered by
        editing the page.
      </P>
      <Table
        head={['Trigger', 'Fires']}
        rows={[
          [<C key="a">onLoad</C>, 'When the form opens'],
          [<C key="b">onChange</C>, 'When an answer changes'],
          [<C key="c">onPageEnter</C>, 'On arriving at a page'],
          [<C key="d">onPageSubmit</C>, 'On leaving a page'],
          [<C key="e">onSubmit</C>, 'At submission'],
        ]}
      />
      <Table
        head={['Action', 'Does']}
        rows={[
          [<C key="a">show / hide</C>, 'Reveals or removes a question, group or page'],
          [<C key="b">enable / disable</C>, 'Locks a question without hiding it'],
          [<C key="c">setRequired</C>, 'Makes an answer mandatory conditionally'],
          [<C key="d">setValue / clearValue</C>, 'Writes or clears an answer'],
          [<C key="e">setChoices</C>, 'Replaces the options offered'],
          [<C key="f">jumpTo / skipPage</C>, 'Changes the route through the form'],
          [<C key="g">screenOut</C>, 'Ends the form early — a disqualification'],
          [<C key="h">addTag / setOutcome</C>, 'Labels the response, or selects which ending is shown'],
        ]}
      />
      <Note>
        A reversible rule undoes itself when its condition stops being true. Leave it
        reversible unless you specifically want a one-way change.
      </Note>

      <H2 id="scoring">Scoring and outcomes</H2>
      <P>
        For quizzes and assessments. Points are set per question, and per-question scoring
        can be exact, partial credit, per choice, a numeric range, an expression, or left for
        manual marking.
      </P>
      <Table
        head={['Reveal mode', 'The respondent sees the score']}
        rows={[
          [<C key="a">never</C>, 'Not at all'],
          [<C key="b">afterSubmit</C>, 'Once the form is submitted'],
          [<C key="c">perQuestion</C>, 'As they answer each question'],
          [<C key="d">afterReview</C>, 'Once somebody has marked it'],
        ]}
      />
      <P>
        <strong>Bands</strong> turn a score into a label — “Pass”, “Distinction” — and can
        select an outcome. <strong>Outcomes</strong> are the endings: each has a condition,
        and the last must have none, because that is the fallback. Publishing fails without
        one.
      </P>

      <H2 id="settings">Form settings</H2>
      <Table
        head={['Group', 'Controls']}
        rows={[
          ['Navigation', 'Progress indicator (bar, steps, percent, none), back button, question numbers, one question per page, auto-advance'],
          ['Behaviour', 'Save and resume with an expiry, autosave interval, multiple submissions, response editing, shuffling, a confirm step, whether hidden answers are retained'],
          ['Limits', 'Response cap, open and close dates, a time limit, and one-per-identity by cookie, IP, identity or email'],
          ['Protection', 'Captcha (off, adaptive, always), honeypot, minimum completion time, required signed token, invite-only'],
          ['Completion', 'Show an outcome or redirect, a redirect delay, and a receipt email to an answered address'],
        ]}
      />
      <Note tone="warn">
        The response cap is read from sharded counters, so it is approximate under
        concurrency — a burst can overshoot by a few. Do not rely on it where the exact
        number is contractual.
      </Note>

      <H2 id="themes">Themes</H2>
      <P>
        A theme sets colour, typography, radius and width tokens for the rendered form, and
        overrides those custom properties and nothing else — so a theme cannot break the
        layout or the accessibility work underneath it. Preview before applying, and set one
        per form or let it fall back to the workspace default.
      </P>

      <H2 id="translations">Translations</H2>
      <P>
        Lists every translatable string: the form's own content, and the interface strings
        the runtime draws — a French form with an English Submit button is not a translated
        form. A locale overrides only what it names, so a partial translation degrades to
        English per string rather than failing or showing a key.
      </P>
      <P>
        Progress is measured against content strings only; interface strings are shared and
        excluded, or a two-question form would report itself mostly translated with both
        questions done.
      </P>

      <H2 id="share">Share and embed</H2>
      <UL>
        <li><strong>Hosted link</strong> — a page FormForge serves. Optionally protected by an access code.</li>
        <li><strong>Embed</strong> — a snippet for your own site, in one of six presentation modes.</li>
        <li><strong>Origins</strong> — the sites allowed to host that embed. A copied snippet will not work elsewhere.</li>
        <li><strong>Signed token</strong> — require one, so only your signed-in users can answer.</li>
      </UL>
      <P>The full integration detail is in the <strong>Setup guide</strong>.</P>

      <H2 id="invites">Single-use links</H2>
      <P>
        One link per invited person, usable once. Combined with the invite-only protection
        setting, this is how you run a form that must not be forwarded — the link is the
        credential, and spending it closes it.
      </P>

      <H2 id="responses">Responses</H2>
      <P>
        Per form, or across forms in the <strong>Responses</strong> inbox. A response opens
        as a panel showing every answer, its files, and its metadata — channel, duration,
        version answered, and completion state.
      </P>
      <UL>
        <li><strong>CSV</strong> for the whole set; <strong>PDF</strong> for one response.</li>
        <li><strong>Copy JSON</strong> for the raw document, which is what the API returns.</li>
        <li>Deleting a response is recorded in the audit log.</li>
      </UL>
      <Note tone="warn">
        A file marked <C>pending</C> was never uploaded — the respondent chose it and the
        transfer did not finish. And uploaded files carry a scan status but nothing has
        scanned them yet; treat them as untrusted.
      </Note>

      <H2 id="analytics">Analytics</H2>
      <P>
        Completion, drop-off by page, duration and per-question breakdowns. Figures come from
        daily rollups with a live tail for the current day, so the numbers are current
        without re-reading every raw row. Everything stored is a sum rather than an average,
        which is what makes any date range answerable by adding days together.
      </P>

      <H2 id="companies">Companies and workspaces</H2>
      <P>
        Add a company for each part of the organisation; each gets a default workspace.
        Members can be scoped to particular companies — someone with no company rows sees
        them all, which means a single-company account needs no configuration at all.
      </P>
      <Note>
        The default company cannot be archived. Make another one the default first.
      </Note>

      <H2 id="team">Team, roles and access</H2>
      <UL>
        <li>Invite by email; invitations expire and can be revoked.</li>
        <li>Roles decide who may read, create, publish and administer.</li>
        <li>Two-factor authentication can be required for the whole tenant.</li>
        <li>Active sessions can be reviewed and ended.</li>
      </UL>

      <H2 id="keys">API keys and webhooks</H2>
      <P>
        Keys are shown once, stored as a hash, and can be scoped and restricted to network
        ranges. Webhooks push events to your systems instead of you polling. Both are covered
        properly in the <strong>Developer documentation</strong>.
      </P>

      <H2 id="audit">Audit log</H2>
      <P>
        Who changed what, and when — form created, updated, published, closed and deleted;
        responses deleted and exported; company and member changes. Filterable, and the place
        to start with “why did this form change?”.
      </P>

      <H2 id="keyboard">Keyboard</H2>
      <Table
        head={['Shortcut', 'Does']}
        rows={[
          [<C key="k">⌘K</C>, 'Command palette — search forms, jump to a screen, switch company, open these docs'],
          [<C key="s">⌘S</C>, 'Save the form you are editing'],
          [<C key="z">⌘Z</C>, 'Undo in the builder'],
          [<C key="y">⌘⇧Z</C>, 'Redo in the builder'],
          [<C key="e">Enter</C>, 'Open the selected form from the list'],
          [<C key="esc">Esc</C>, 'Close the palette, a menu or an overlay'],
        ]}
      />

      <H2 id="lifecycle">The life of a form</H2>
      <OL>
        <li><strong>Create</strong> — name, type and workspace; a draft is created with it.</li>
        <li><strong>Build</strong> — questions, pages, logic. Autosaves.</li>
        <li><strong>Validate</strong> — publishing runs the validator; errors block, warnings do not.</li>
        <li><strong>Publish</strong> — the draft becomes an immutable version and goes live.</li>
        <li><strong>Distribute</strong> — hosted link, embed, or single-use invitations.</li>
        <li><strong>Collect</strong> — responses arrive against the version answered.</li>
        <li><strong>Close</strong> — submissions stop; the data stays.</li>
      </OL>
      <Note tone="warn">
        Editing a published form edits its <em>draft</em>. Nothing a respondent sees changes
        until you publish again — including anyone part-way through a saved session.
      </Note>

      <H2 id="limits">What is not finished</H2>
      <P>
        Some surfaces imply more than is wired up. The current list is kept by hand in{' '}
        <C>docs/STATUS.md</C> under “silent gaps” — things whose schema, settings or UI exist
        while the behaviour behind them does not. Read it before depending on a feature.
      </P>
    </>
  );
}
